import { nextTick, onBeforeUnmount, onMounted, ref, watch, type Ref } from 'vue';
import {
  parse,
  getYearMonth,
  buildDateRanges,
  stringifyDayHeader as formatDayHeader,
} from 'src/lib/calendarHelper';
import type { CalendarProps, CalendarState, DateRangeItem } from 'src/models/calendar';

interface CalendarPaneDeps {
  stringify: (date: Date, format?: string) => string;
  onInput: () => void;
}

export interface CalendarPaneContext {
  rootEl: Ref<HTMLElement | undefined>;
  close: () => void;
  inputClick: () => void;
  switchMonthView: () => void;
  switchDecadeView: () => void;
  yearSelect: (year: number) => void;
  monthSelect: (year: number, index: number) => void;
  daySelect: (date: Date, event?: MouseEvent) => void;
  preNextDecadeClick: (flag: number) => void;
  preNextMonthClick: (flag: number) => void;
  preNextYearClick: (flag: number) => void;
  getItemClasses: (d: DateRangeItem) => string;
  getSpecialDay: (v: Date) => string;
  stringifyDayHeader: (date: Date, month?: number) => string;
  setToday: () => void;
  parseDateWithDefault: (val: string) => Date;
}

// placeholderのみdefaultを持たない真のoptionalなpropsのため、Requiredの対象から除外する
type CalendarPaneProps = Required<Omit<CalendarProps, 'placeholder'>> & Pick<CalendarProps, 'placeholder'>;

export function useCalendarPane(
  props: CalendarPaneProps,
  state: CalendarState,
  deps: CalendarPaneDeps,
): CalendarPaneContext {
  const rootEl = ref<HTMLElement>();

  const close = (): void => {
    state.view = 'none';
  };

  const updatePaneStyle = (): void => {
    if (state.view === 'month' || state.view === 'year') return;

    nextTick(() => {
      if (!rootEl.value) return;

      const offsetLeft = rootEl.value.offsetLeft;
      const offsetWidth =
        (rootEl.value.querySelector('.datepicker-inner') as HTMLElement | null)?.offsetWidth ?? 0;
      const popWidth = props.pane * offsetWidth + props.borderWidth;
      state.paneStyle.width = popWidth + 'px';
      if (props.hasInput) {
        if (popWidth + offsetLeft > document.documentElement.clientWidth) {
          state.paneStyle.right = '0px';
        }
      } else {
        state.paneStyle.position = 'initial';
      }
    });
  };

  const inputClick = (): void => {
    state.currDate = parse(state.inputValue) || new Date();
    if (state.view !== 'month' && state.view !== 'year') {
      state.view = state.view === 'day' ? 'none' : 'day';
    }
    updatePaneStyle();
  };

  const switchMonthView = (): void => {
    if (props.disableSwitchMonthView) return;

    state.view = 'month';
  };

  const switchDecadeView = (): void => {
    state.view = 'year';
  };

  const yearSelect = (year: number): void => {
    state.view = 'month';
    state.currDate = new Date(year, state.currDate.getMonth(), state.currDate.getDate());
  };

  const monthSelect = (year: number, index: number): void => {
    state.view = 'day';
    state.currDate = new Date(year, index, state.currDate.getDate());
    props.changePane(year, index, props.pane);
  };

  const daySelect = (date: Date, event?: MouseEvent): void => {
    if (event?.target && (event.target as HTMLElement).classList[0] === 'datepicker-item-disable') {
      return;
    }
    if (!props.hasInput) {
      props.onDayClick(date, deps.stringify(date));
      return;
    }
    state.currDate = date;
    state.inputValue = deps.stringify(state.currDate);
    deps.onInput();
    state.view = 'none';
  };

  const blur = (e: MouseEvent): void => {
    if (rootEl.value && !rootEl.value.contains(e.target as Node) && props.hasInput) close();
  };

  const refreshDateRanges = (): void => {
    const ranges = buildDateRanges(
      state.currDate,
      props.pane,
      props.disabledDaysOfWeek,
      state.inputValue,
    );
    state.dateRange = ranges.dateRange;
    state.decadeRange = ranges.decadeRange;
  };

  watch(() => state.currDate, refreshDateRanges);

  const preNextDecadeClick = (flag: number): void => {
    const year = state.currDate.getFullYear();
    const months = state.currDate.getMonth();
    const date = state.currDate.getDate();
    state.currDate = flag === 0
      ? new Date(year - 10, months, date)
      : new Date(year + 10, months, date);
  };

  const preNextMonthClick = (flag: number): void => {
    const year = state.currDate.getFullYear();
    const month = state.currDate.getMonth();
    const date = state.currDate.getDate();
    if (flag === 0) {
      const preMonth = getYearMonth(year, month - 1);
      state.currDate = new Date(preMonth.year, preMonth.month, date);
      props.changePane(preMonth.year, preMonth.month, props.pane);
    } else {
      const nextMonth = getYearMonth(year, month + 1);
      state.currDate = new Date(nextMonth.year, nextMonth.month, date);
      props.changePane(nextMonth.year, nextMonth.month, props.pane);
    }
  };

  const preNextYearClick = (flag: number): void => {
    const year = state.currDate.getFullYear();
    const months = state.currDate.getMonth();
    const date = state.currDate.getDate();
    state.currDate = flag === 0
      ? new Date(year - 1, months, date)
      : new Date(year + 1, months, date);
  };

  const getItemClasses = (d: DateRangeItem): string => {
    const clazz = [];
    clazz.push(d.sclass);
    const now = new Date();
    if (now.getFullYear() === d.date.getFullYear() &&
      now.getMonth() === d.date.getMonth() &&
      now.getDate() === d.date.getDate()) {
      clazz.push('today');
    }
    return clazz.join(' ');
  };

  const getSpecialDay = (v: Date): string => props.specialDays[deps.stringify(v)];

  const stringifyDayHeader = (date: Date, month = 0): string =>
    formatDayHeader(date, month, props.dayHeaderFormat);

  const setToday = (): void => { daySelect(new Date()); };

  const parseDateWithDefault = (val: string): Date => parse(val) ?? new Date();

  onMounted(() => {
    state.currDate = parse(state.inputValue) || new Date();
    const year = state.currDate.getFullYear();
    const month = state.currDate.getMonth();
    props.changePane(year, month, props.pane);
    if (!props.hasInput) {
      state.view = 'day';
      updatePaneStyle();
    }
    document.addEventListener('click', blur);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('click', blur);
  });

  return {
    rootEl,
    close,
    inputClick,
    switchMonthView,
    switchDecadeView,
    yearSelect,
    monthSelect,
    daySelect,
    preNextDecadeClick,
    preNextMonthClick,
    preNextYearClick,
    getItemClasses,
    getSpecialDay,
    stringifyDayHeader,
    setToday,
    parseDateWithDefault,
  };
}
